import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { FormShell, Field, CurrencyInput, AutocompleteInput } from "@/components/forms/controls";
import EditableSelect from "@/components/EditableSelect";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const useList = (entity) => useQuery({ queryKey: ["entity", entity], queryFn: async () => (await api.get(`/${entity}`)).data });

export default function AddExternal() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const jobs = useList("jobs");
  const external = useList("external");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    job_id: "", process_name: "", vendor_name: "",
    planned_cost: "", actual_cost: "", date_sent: todayISO(), date_received: "", status: "Pending",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const vendorOptions = (external.data || []).map((e) => e.vendor_name);

  const onReceivedChange = (v) => {
    setForm((p) => ({ ...p, date_received: v, status: v ? "Completed" : (p.status === "Completed" ? "Pending" : p.status) }));
  };

  const submit = async () => {
    if (!form.job_id || !form.process_name) {
      toast.error("Job and Process Name are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/external", form);
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success(`External process ${data.code} added`);
      navigate(`/jobs/${form.job_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormShell title="Add External Process" subtitle="Track outsourced work sent to vendors.">
      <Field label="Job" required>
        <Select value={form.job_id} onValueChange={(v) => set("job_id", v)}>
          <SelectTrigger data-testid="ext-job"><SelectValue placeholder="Select job..." /></SelectTrigger>
          <SelectContent>
            {(jobs.data || []).map((j) => <SelectItem key={j.id} value={j.id}>{j.code} · {j.job_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Process Name" required>
        <EditableSelect optionsKey="external_process_name" value={form.process_name} onChange={(v) => set("process_name", v)} testId="ext-process" />
      </Field>

      <Field label="Vendor Name" hint="Start typing to pick a previous vendor">
        <AutocompleteInput value={form.vendor_name} onChange={(v) => set("vendor_name", v)} options={vendorOptions} placeholder="e.g. Precision Heat Treat Co." testId="ext-vendor" />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Planned Cost"><CurrencyInput value={form.planned_cost} onChange={(v) => set("planned_cost", v)} testId="ext-planned" placeholder="0" /></Field>
        <Field label="Actual Cost" hint="Optional — add when received"><CurrencyInput value={form.actual_cost} onChange={(v) => set("actual_cost", v)} testId="ext-actual" placeholder="0" /></Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Date Sent"><Input type="date" value={form.date_sent} onChange={(e) => set("date_sent", e.target.value)} data-testid="ext-sent" /></Field>
        <Field label="Date Received" hint="Leave blank until received"><Input type="date" value={form.date_received} onChange={(e) => onReceivedChange(e.target.value)} data-testid="ext-received" /></Field>
      </div>

      <Field label="Status" hint="Auto-set to Completed when a received date is entered">
        <Select value={form.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger data-testid="ext-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["Pending", "In Progress", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={() => navigate("/")} className="flex-1" data-testid="ext-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="ext-submit">
          {saving ? "Saving..." : "Add External Process"}
        </Button>
      </div>
    </FormShell>
  );
}
